<template>
  <div class="upload-container">
    <!-- Top Preview (ONLY show when there is at least 1 image) -->
    <div v-if="showImageTop && images.length" class="image-container centered">
      <div class="preview framed">
        <img :src="images[0].url" alt="Image Preview" class="avatar" />
        <span class="remove-icon" @click="removeImage(0)">×</span>
      </div>
    </div>

    <!-- Upload Button -->
    <div :class="{ 'text-center': showImageTop }">
      <b-button variant="outline-primary" @click="triggerFileInput" class="upload-button">
        {{ buttonText }}
      </b-button>
    </div>

    <!-- Thumbnails Row -->
    <!-- Case A: showImageTop=false => show ALL images here -->
    <!-- Case B: showImageTop=true & multiple=true => show images from index 1 onwards -->
    <div v-if="shouldShowThumbRow" class="image-container-row">
      <div
        v-for="(image, idx) in thumbImages"
        :key="image.uuid || image.url || idx"
        class="preview framed mb-1"
      >
        <img :src="image.url" alt="Image Preview" class="avatar" />
        <span class="remove-icon" @click="removeImage(idx + thumbOffset)">×</span>
      </div>
    </div>

    <!-- Hidden File Input -->
    <input
      type="file"
      ref="fileInput"
      @change="onFileChange"
      :multiple="multiple"
      accept="image/*"
      style="display: none;"
    />
  </div>
</template>

<script>
export default {
  props: {
    multiple: { type: Boolean, default: false },
    showImageTop: { type: Boolean, default: true },
    buttonText: { type: String, default: "Upload Image" },
    // can be: ["url"], [{uuid,url}], etc
    initialImages: { type: Array, default: () => [] },
  },

  data() {
    return {
      // normalized: [{ uuid, file, url, isObjectUrl }]
      images: [],

      // guard so parent updating initialImages doesn't overwrite local preview
      isDirtyLocalPick: false,

      // if user removed all, don't re-show old initial unless parent actually changes
      userCleared: false,
      lastInitialSignature: "",
    };
  },

  computed: {
    thumbOffset() {
      // when top exists, thumbs start from index 1
      return this.showImageTop ? 1 : 0;
    },

    thumbImages() {
      if (!this.images.length) return [];
      if (!this.showImageTop) return this.images;
      // showImageTop=true: thumbnails = images after first
      return this.images.slice(1);
    },

    shouldShowThumbRow() {
      if (!this.images.length) return false;

      // show all row if showImageTop is false
      if (!this.showImageTop) return true;

      // showImageTop=true: show row only when multiple=true and have >1 image
      return this.multiple && this.images.length > 1;
    },
  },

  watch: {
    initialImages: {
      immediate: true,
      handler(newVal) {
        const normalized = this.normalizeImages(newVal);
        const sig = JSON.stringify(normalized.map((x) => x.url));

        // If user cleared and initial didn't change, keep hidden
        if (this.userCleared && sig === this.lastInitialSignature) return;

        // If local picked, only accept parent update when it becomes real remote urls
        if (this.isDirtyLocalPick) {
          const hasRemote = normalized.some((x) => /^https?:\/\//.test(x.url));
          if (hasRemote && sig !== this.lastInitialSignature) {
            this.cleanupObjectUrls();
            this.images = normalized;
            this.isDirtyLocalPick = false;
            this.userCleared = false;
            this.lastInitialSignature = sig;
          }
          return;
        }

        // Normal sync from parent
        this.cleanupObjectUrls();
        this.images = normalized;
        this.userCleared = normalized.length === 0 ? this.userCleared : false;
        this.lastInitialSignature = sig;
      },
    },
  },

  beforeDestroy() {
    this.cleanupObjectUrls();
  },

  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    extractUrl(x) {
      if (!x) return null;
      if (typeof x === "string") return x;

      if (typeof x === "object") {
        if (typeof x.url === "string") return x.url;
        if (typeof x.src === "string") return x.src;
        if (typeof x.path === "string") return x.path;

        if (x.url && typeof x.url === "object") {
          if (typeof x.url.url === "string") return x.url.url;
          if (typeof x.url.src === "string") return x.url.src;
          if (typeof x.url.path === "string") return x.url.path;
        }
      }
      return null;
    },

    normalizeImages(list) {
      const arr = Array.isArray(list) ? list : [];
      return arr
        .map((item) => {
          const url = this.extractUrl(item);
          if (!url) return null;

          const uuid = item && typeof item === "object" ? (item.uuid || item.id || null) : null;
          const file = item && typeof item === "object" ? (item.file || null) : null;

          return { uuid, file, url, isObjectUrl: false };
        })
        .filter(Boolean);
    },

    cleanupObjectUrls() {
      for (const img of this.images) {
        if (img && img.isObjectUrl && typeof img.url === "string" && img.url.startsWith("blob:")) {
          try {
            URL.revokeObjectURL(img.url);
          } catch (e) {
            // noop: already revoked/invalid
          }
        }
      }
    },

    onFileChange(event) {
      const files = event.target.files;
      if (!files || !files.length) return;

      this.userCleared = false;
      this.isDirtyLocalPick = true;

      if (!this.multiple) {
        this.cleanupObjectUrls();
        this.images = [];
      }

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const blobUrl = URL.createObjectURL(file);
        const obj = { uuid: null, file, url: blobUrl, isObjectUrl: true };

        if (this.multiple) this.images.push(obj);
        else this.images = [obj];
      }

      this.$emit("images-changed", [...this.images]);

      // reset input to allow reselect same file
      this.$refs.fileInput.value = "";
    },

    removeImage(index) {
      const removed = this.images[index];

      if (removed && removed.isObjectUrl && removed.url && removed.url.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(removed.url);
        } catch (e) {
          // noop
        }
      }

      this.images.splice(index, 1);

      this.$emit("images-removed", removed);
      this.$emit("images-changed", [...this.images]);

      // ✅ if empty => whole preview/frame disappears
      if (!this.images.length) {
        this.userCleared = true;
        this.isDirtyLocalPick = false;
      }

      this.$refs.fileInput.value = "";
    },

    clear() {
      this.cleanupObjectUrls();
      this.images = [];
      this.userCleared = true;
      this.isDirtyLocalPick = false;

      if (this.$refs.fileInput) this.$refs.fileInput.value = "";

      this.$emit("images-changed", []);
      this.$emit("images-removed", null);
    },
  },
};
</script>

<style scoped>
.upload-container {
  text-align: left;
}

.image-container.centered {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.image-container-row {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.preview {
  position: relative;
  display: inline-block;
  text-align: center;
  margin-right: 10px;
}

.framed {
  padding: 20px;
  border: 2px solid #ddd;
  border-radius: 10px;
  background-color: white;
}

.preview img {
  max-width: 100px;
  max-height: 100px;
  display: block;
  border-radius: 50%;
}

.remove-icon {
  position: absolute;
  bottom: 5px;
  right: 5px;
  color: white;
  background-color: red;
  border-radius: 50%;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 16px;
  line-height: 16px;
}

.upload-button {
  margin-bottom: 10px;
}
</style>
